import React from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PaidIcon from '@mui/icons-material/Paid';
import CampaignIcon from '@mui/icons-material/Campaign';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import TuneIcon from '@mui/icons-material/Tune';
import { Link } from 'react-router-dom';
import { useCompetitions } from './CompetitionContext';

const adminTiles = [
  {
    title: 'Sparingi',
    description: 'Przeglądaj znalezione mecze towarzyskie, dopasowuj drużyny i zatwierdzaj zmiany rankingu Elo.',
    link: '/janekjot/friendlies',
    icon: SportsSoccerIcon,
    color: '#66bb6a',
  },
  {
    title: 'Dodaj mecz',
    description: 'Wprowadź ręcznie nowe spotkanie, wynik i parametry potrzebne do aktualizacji danych oraz Elo.',
    link: '/janekjot/addMatch',
    icon: AddCircleOutlineIcon,
    color: '#42a5f5',
  },
  {
    title: 'Zarządzanie symulacjami',
    description: 'Uruchamiaj pobieranie danych i symulacje lig bieżącego sezonu oraz usuwaj nieaktualne wyniki.',
    link: '/janekjot/manageSimulations',
    icon: ManageSearchIcon,
    color: '#ab47bc',
  },
  {
    title: 'Plan social media',
    description: 'Ustal dzienne tryby publikacji dla lig, sprawdzaj kandydatow i watchliste meczow statystycznych.',
    link: '/janekjot/social-plan',
    icon: CampaignIcon,
    color: '#26a69a',
  },
  {
    title: 'Utworz post',
    description: 'Ręcznie wygeneruj raport meczowy lub podsumowanie i wyślij propozycję do obecnego flow publikacji.',
    link: '/janekjot/createPost',
    icon: CampaignIcon,
    color: '#5c6bc0',
  },
  {
    title: 'Drużyny bez meczów',
    description: 'Znajdź kluby z aktywnego sezonu, które są przypisane do ligi, ale nie mają żadnego meczu.',
    link: '/janekjot/druzyny-bez-meczow',
    icon: ReportProblemIcon,
    color: '#ff7043',
  },
  {
    title: 'Ligi bez najbliższych meczów',
    description: 'Sprawdź ligi aktywnego sezonu bez zaplanowanego meczu w wybranym oknie dni.',
    link: '/janekjot/ligi-bez-najblizszych-meczow',
    icon: ManageSearchIcon,
    color: '#29b6f6',
  },
  {
    title: 'Mecze przeszłe bez wyniku',
    description: 'Znajdź zaplanowane mecze, których termin już minął, ale wynik nie został jeszcze zapisany.',
    link: '/janekjot/mecze-przeszle-bez-wyniku',
    icon: ReportProblemIcon,
    color: '#ec407a',
  },
  {
    title: 'Błędne zmiany Elo',
    description: 'Sprawdź mecze, w których zmiana Elo ma znak niezgodny z wynikiem, i zatwierdzaj korekty po podglądzie.',
    link: '/janekjot/bledne-zmiany-elo',
    icon: ReportProblemIcon,
    color: '#ffca28',
  },
  {
    title: 'Losowania Europejskich Pucharów',
    description: 'Wprowadzaj po losowaniu po cztery mecze domowe i wyjazdowe dla klubów w LM, LE i LK.',
    link: '/janekjot/europeDraws',
    icon: ShuffleIcon,
    color: '#7e57c2',
  },
  {
    title: 'Kursy bukmacherów MŚ',
    description: 'Porównuj kursy i prawdopodobieństwa dla spotkań mistrzostw świata z wynikami modelu.',
    link: '/janekjot/odds',
    icon: PaidIcon,
    color: '#ffa726',
  },
  {
    title: 'Nowy sezon',
    description: 'Odkrywaj rozgrywki, mapuj zespoły i przygotuj strukturę ligową przed rozpoczęciem sezonu.',
    link: '/janekjot/seasonImport',
    icon: EventRepeatIcon,
    color: '#26c6da',
  },
  {
    title: 'League Manager',
    description: 'Edytuj konfigurację ligi, zasady awansów i spadków, wymagania dostępu oraz ustawienia baraży.',
    link: '/janekjot/leagueManager',
    icon: TuneIcon,
    color: '#ef5350',
  },
  {
    title: 'Ustawienia aplikacji',
    description: 'Zarządzaj centralnymi parametrami systemu, w tym wagą Elo, wartością drużyn i aktywnym sezonem.',
    link: '/janekjot/settings',
    icon: SettingsSuggestIcon,
    color: '#ffee58',
  },
];

const AdminPanel = () => {
  const { loading } = useCompetitions();

  if (loading) {
    return <Typography variant="body1" sx={{ textAlign: 'center', py: 5 }}>Ładowanie...</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1 }}>
          Panel administracyjny
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Centrum zarządzania rozgrywkami, symulacjami i konfiguracją SymuLigi.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {adminTiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={tile.link}>
              <Card
                component={Link}
                to={tile.link}
                sx={{
                  height: '100%',
                  minHeight: 190,
                  display: 'flex',
                  color: 'inherit',
                  textDecoration: 'none',
                  backgroundColor: '#2c2c2c',
                  border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    backgroundColor: '#363636',
                    borderColor: tile.color,
                  },
                }}
              >
                <CardContent
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    p: 3,
                    '&:last-child': { pb: 3 },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      mb: 1.5,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      backgroundColor: `${tile.color}18`,
                    }}
                  >
                    <Icon sx={{ color: tile.color, fontSize: 30 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {tile.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {tile.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AdminPanel;
